import React from 'react';

import { useStore } from 'react-redux';

import PropTypes from 'prop-types';

import Button from '@asteria/component-core/button';
import {
	Text,
	TextGroup,
	UnorderedList,
} from '@asteria/component-core/typography';
import Wrapper, {
	Content,
	Footer,
	FooterSection,
	Header,
} from '@asteria/component-core/wrapper';

import Modal from '@asteria/component-modal';

import * as IntegrationStore from '@asteria/datalayer/stores/integrations';

import { Translation, useTranslate } from '@asteria/language';
import { cn } from '@asteria/utils-funcs/classes';

import { useIntegrationName } from '../hooks';

/**
 * @typedef Props
 * @property { boolean } open
 * @property { string } className
 * @property { () => void } onClose
 * @property { (object: { type: string; key: string }) => void } onContinue
 * @property { string } type
 * @property { string } name
 */

export function useDuplicateModalState(onSuccess) {
	const store = useStore();

	const [state, setState] = React.useState(null);

	const onClose = React.useCallback(() => {
		setState(null);
	}, []);

	const onContinue = React.useCallback(
		({ type, key }) => onSuccess?.({ type, key }),
		[onSuccess],
	);

	const onValidate = React.useCallback(
		({ type, key }) => {
			const available = IntegrationStore.selectors.integrations(
				store.getState(),
				{ type, key },
			);

			if (available?.length) {
				setState({ type, key });

				return false;
			}

			return onSuccess?.({ type, key });
		},
		[onSuccess, store],
	);

	return [
		state,
		React.useMemo(
			() => ({
				close: onClose,
				continue: onContinue,
				validate: onValidate,
			}),
			[onClose, onContinue, onValidate],
		),
	];
}

/** @type { React.FC<Props> } */
const OnboardingDuplicateModal = React.memo(function OnboardingDuplicateModal(
	props,
) {
	const { open, className, onClose, onContinue, type, name: key } = props;

	const { t } = useTranslate();

	const name = useIntegrationName(type, key);

	const translationOptions = React.useMemo(
		() => ({
			postfix: { variant: 'duplicate' },
			data: { integrationName: name },
		}),
		[name],
	);

	return (
		<Modal
			open={open}
			className={cn(
				'asteria-component__onboarding-duplicate-modal',
				className,
			)}
			onClose={onClose}
		>
			<Wrapper>
				<Header onClose={onClose}>
					{t(['onboarding.modal.title'], translationOptions)}
				</Header>
				<Content className="flex flex-col gap-4">
					<TextGroup className="flex flex-col gap-2">
						<Translation
							translationKey="onboarding.modal.content.0"
							translationOptions={translationOptions}
							Component={Text}
						/>
						<Translation
							translationKey="onboarding.modal.content.1"
							translationOptions={translationOptions}
							Component={Text}
						/>
					</TextGroup>
					<TextGroup className="flex flex-col gap-2">
						<Translation
							translationKey="onboarding.modal.content.2"
							translationOptions={translationOptions}
							Component={Text}
						/>
						<UnorderedList
							items={[
								{
									value: t(
										['onboarding.modal.bullet.0'],
										translationOptions,
									),
								},
								{
									value: t(
										['onboarding.modal.bullet.1'],
										translationOptions,
									),
								},
								{
									value: t(
										['onboarding.modal.bullet.2'],
										translationOptions,
									),
								},
							]}
						/>
					</TextGroup>
					<TextGroup className="flex flex-col gap-2">
						<Translation
							translationKey="onboarding.modal.content.3"
							translationOptions={translationOptions}
							Component={Text}
						/>
						<UnorderedList
							items={[
								{
									value: t(
										['onboarding.modal.bullet.3'],
										translationOptions,
									),
								},
								{
									value: t(
										['onboarding.modal.bullet.4'],
										translationOptions,
									),
								},
							]}
						/>
					</TextGroup>
					<Translation
						translationKey="onboarding.modal.content.4"
						translationOptions={translationOptions}
						Component={Text}
					/>
				</Content>
				<Footer>
					<FooterSection position="first">
						<Button
							variant="tertiary"
							label={t(
								['onboarding.modal.action.action-close'],
								translationOptions,
							)}
							onClick={onClose}
						/>
					</FooterSection>
					<FooterSection position="last">
						<Button
							variant="primary"
							label={t(
								['onboarding.modal.action.action-continue'],
								translationOptions,
							)}
							onClick={() => onContinue?.({ type, key })}
						/>
					</FooterSection>
				</Footer>
			</Wrapper>
		</Modal>
	);
});

OnboardingDuplicateModal.displayName = 'OnboardingDuplicateModal';

OnboardingDuplicateModal.propTypes = {
	open: PropTypes.bool,
	className: PropTypes.string,
	onClose: PropTypes.func,
	onContinue: PropTypes.func,
	type: PropTypes.string,
	name: PropTypes.string,
};

export default OnboardingDuplicateModal;
